import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { useLanguage } from '../hooks/useLanguage';
import { matomoAnalyticsSections } from '../lib/cookieConsentMatomo';
import { syncMatomoWithConsent } from '../lib/matomo';
import { syncGoogleAnalyticsWithConsent } from '../lib/googleAnalytics';

function syncAnalyticsWithConsent(acceptedAnalytics) {
  syncMatomoWithConsent(acceptedAnalytics);
  syncGoogleAnalyticsWithConsent(acceptedAnalytics);
}

const CookieConsentComponent = () => {
  const { language } = useLanguage();

  useEffect(() => {
    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom left',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },
      onFirstConsent: () => {
        syncAnalyticsWithConsent(CookieConsent.acceptedCategory('analytics'));
      },
      onConsent: () => {
        syncAnalyticsWithConsent(CookieConsent.acceptedCategory('analytics'));
      },
      onChange: () => {
        syncAnalyticsWithConsent(CookieConsent.acceptedCategory('analytics'));
      },
      categories: {
        necessary: {
          readOnly: true,
          enabled: true
        },
        analytics: {
          enabled: false,
          autoClear: {
            cookies: [{ name: /^_pk_/ }, { name: /^_ga/ }, { name: '_gid' }],
          },
        },
        marketing: {
          enabled: false
        }
      },
      language: {
        default: language,
        autoDetect: 'browser',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept all", you consent to our use of cookies.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
              footer: '<a href="/privacy-policy">Privacy Policy</a>\n<a href="/cookie-policy">Cookie Policy</a>'
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.'
                },
                {
                  title: 'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
                  description: 'These cookies are essential for the proper functioning of the website. Without these cookies, the website would not work properly.',
                  linkedCategory: 'necessary'
                },
                matomoAnalyticsSections.en,
                {
                  title: 'Marketing Cookies',
                  description: 'These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.',
                  linkedCategory: 'marketing'
                },
                {
                  title: 'More information',
                  description: 'For any queries in relation to our policy on cookies and your choices, please <a class="cc__link" href="#contact">contact us</a>.'
                }
              ]
            }
          },
          es: {
            consentModal: {
              title: 'Usamos cookies',
              description: 'Utilizamos cookies para mejorar su experiencia de navegación, ofrecer contenido personalizado y analizar nuestro tráfico. Al hacer clic en "Aceptar todo", usted acepta nuestro uso de cookies.',
              acceptAllBtn: 'Aceptar todo',
              acceptNecessaryBtn: 'Rechazar todo',
              showPreferencesBtn: 'Gestionar preferencias',
              footer: '<a href="/privacy-policy">Política de Privacidad</a>\n<a href="/cookie-policy">Política de Cookies</a>'
            },
            preferencesModal: {
              title: 'Preferencias de Cookies',
              acceptAllBtn: 'Aceptar todo',
              acceptNecessaryBtn: 'Rechazar todo',
              savePreferencesBtn: 'Guardar preferencias',
              closeIconLabel: 'Cerrar',
              serviceCounterLabel: 'Servicio|Servicios',
              sections: [
                {
                  title: 'Uso de Cookies',
                  description: 'Utilizamos cookies para garantizar las funcionalidades básicas del sitio web y mejorar su experiencia en línea. Puede elegir para cada categoría activar/desactivar cuando lo desee.'
                },
                {
                  title: 'Cookies Estrictamente Necesarias <span class="pm__badge">Siempre Activadas</span>',
                  description: 'Estas cookies son esenciales para el correcto funcionamiento del sitio web. Sin estas cookies, el sitio web no funcionaría correctamente.',
                  linkedCategory: 'necessary'
                },
                matomoAnalyticsSections.es,
                {
                  title: 'Cookies de Marketing',
                  description: 'Estas cookies se utilizan para rastrear visitantes en sitios web. La intención es mostrar anuncios que sean relevantes y atractivos para el usuario individual.',
                  linkedCategory: 'marketing'
                },
                {
                  title: 'Más información',
                  description: 'Para cualquier consulta en relación con nuestra política de cookies y sus opciones, por favor <a class="cc__link" href="#contact">contáctenos</a>.'
                }
              ]
            }
          },
          fr: {
            consentModal: {
              title: 'Nous utilisons des cookies',
              description: 'Nous utilisons des cookies pour améliorer votre expérience de navigation, proposer du contenu personnalisé et analyser notre trafic. En cliquant sur "Tout accepter", vous consentez à notre utilisation des cookies.',
              acceptAllBtn: 'Tout accepter',
              acceptNecessaryBtn: 'Tout refuser',
              showPreferencesBtn: 'Gérer les préférences',
              footer: '<a href="/privacy-policy">Politique de Confidentialité</a>\n<a href="/cookie-policy">Politique des Cookies</a>'
            },
            preferencesModal: {
              title: 'Préférences des Cookies',
              acceptAllBtn: 'Tout accepter',
              acceptNecessaryBtn: 'Tout refuser',
              savePreferencesBtn: 'Enregistrer les préférences',
              closeIconLabel: 'Fermer',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Utilisation des Cookies',
                  description: 'Nous utilisons des cookies pour garantir les fonctionnalités de base du site Web et améliorer votre expérience en ligne. Vous pouvez choisir pour chaque catégorie d\'activer/désactiver quand vous le souhaitez.'
                },
                {
                  title: 'Cookies Strictement Nécessaires <span class="pm__badge">Toujours Activés</span>',
                  description: 'Ces cookies sont essentiels au bon fonctionnement du site Web. Sans ces cookies, le site Web ne fonctionnerait pas correctement.',
                  linkedCategory: 'necessary'
                },
                matomoAnalyticsSections.fr,
                {
                  title: 'Cookies Marketing',
                  description: 'Ces cookies sont utilisés pour suivre les visiteurs sur les sites Web. L\'intention est d\'afficher des publicités pertinentes et engageantes pour l\'utilisateur individuel.',
                  linkedCategory: 'marketing'
                },
                {
                  title: 'Plus d\'informations',
                  description: 'Pour toute question concernant notre politique en matière de cookies et vos choix, veuillez <a class="cc__link" href="#contact">nous contacter</a>.'
                }
              ]
            }
          }
        }
      }
    });
  }, [language]);

  return null;
};

export default CookieConsentComponent;
